import { Button, DemoDataBanner, Sheet, cn } from '@buakei/ui';
import { LogOut, Menu, MessageSquare, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/auth/session';
import { useInspector } from '@/inspector/inspector-context';
import { SourceInspector } from '@/inspector/SourceInspector';
import { Brand } from './Brand';
import { HeaderSlotProvider } from './header-slot';
import { EXPERIENCE_META, LIBRARY_ITEM, NAV, experienceFromPath, type NavItem } from './nav-config';
import { TABLET_QUERY, WIDE_QUERY, useMediaQuery } from './use-media-query';
import { PrivacyScopeIndicator, WorkspaceSwitcher } from './WorkspaceSwitcher';

/**
 * Primary navigation (ADR-009). `rail`: the desktop icon rail, icon above a short label, with
 * the active item as a solid navy tile. `list`: the same items as rows, for the mobile drawer.
 */
function NavList({
  items,
  variant,
  onNavigate,
}: {
  items: NavItem[];
  variant: 'rail' | 'list';
  onNavigate?: () => void;
}) {
  const core = items.filter((i) => i.stage !== 'later');
  const later = items.filter((i) => i.stage === 'later');
  const rail = variant === 'rail';

  const renderItem = (item: NavItem, muted = false) => (
    <li key={item.to}>
      <NavLink
        to={item.to}
        end
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'group flex rounded-lg transition-colors',
            rail
              ? 'flex-col items-center gap-1.5 px-1 py-2 text-center'
              : 'items-center gap-3 px-3 py-2',
            !rail && (isActive ? 'bg-navy-900 text-white' : 'text-navy-900 hover:bg-navy-50'),
          )
        }
      >
        {({ isActive }) => (
          <>
            <span
              className={cn(
                'flex shrink-0 items-center justify-center rounded-xl transition-colors',
                rail ? 'size-11' : 'size-5',
                rail && isActive && 'bg-navy-900 text-white',
                rail && !isActive && 'text-navy-900 group-hover:bg-navy-50',
                rail && !isActive && muted && 'text-muted-600',
              )}
            >
              <item.icon className="size-5" aria-hidden />
            </span>
            <span
              className={cn(
                rail ? 'text-xs leading-tight' : 'text-sm font-medium',
                rail && (isActive ? 'font-semibold text-navy-900' : 'font-medium text-ink-900'),
                rail && !isActive && muted && 'text-muted-600',
              )}
            >
              {item.label}
            </span>
            {item.stage === 'preview' ? (
              <span
                className={cn(
                  'font-normal',
                  rail ? 'text-[11px] leading-none text-muted-600' : 'ml-auto text-xs',
                  !rail && (isActive ? 'text-white' : 'text-muted-600'),
                )}
              >
                Preview
              </span>
            ) : null}
          </>
        )}
      </NavLink>
    </li>
  );

  return (
    <nav aria-label="Primary" className={cn('flex flex-col', rail ? 'gap-4' : 'gap-5')}>
      <ul className={cn('flex flex-col', rail ? 'gap-2' : 'gap-1')}>
        {core.map((i) => renderItem(i))}
      </ul>
      {later.length > 0 ? (
        <div className={cn('flex flex-col gap-2', rail && 'border-t border-line-200 pt-4')}>
          <p className={cn('text-xs text-muted-600', rail ? 'text-center' : 'px-3')}>
            {rail ? 'Later' : 'Planned modules'}
          </p>
          <ul className={cn('flex flex-col', rail ? 'gap-2' : 'gap-1')}>
            {later.map((i) => renderItem(i, true))}
          </ul>
        </div>
      ) : null}
    </nav>
  );
}

/** Shared AppShell for every signed-in experience (M1-02). */
export function AppShell() {
  const { session, signOut } = useAuth();
  const { citation, close } = useInspector();
  const location = useLocation();
  const navigate = useNavigate();
  const experience =
    experienceFromPath(location.pathname) ?? session?.experiences[0] ?? 'professional';
  const roles = new Set(session?.memberships.flatMap((m) => m.roles));
  // Accounts without a workspace still have the shared library (ADR-016).
  const items = session?.experiences.length
    ? NAV[experience].filter((i) => !i.requiresRole || roles.has(i.requiresRole))
    : [LIBRARY_ITEM];
  const isWide = useMediaQuery(WIDE_QUERY);
  const isTablet = useMediaQuery(TABLET_QUERY);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const inspectorHeadingRef = useRef<HTMLHeadingElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  // Where pages put a control that stays in view, beside Feedback (Ask Buakei's history).
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  // Close the inspector when moving between pages; it belongs to the page that opened it.
  useEffect(() => {
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (citation && isWide) inspectorHeadingRef.current?.focus();
  }, [citation, isWide]);

  return (
    <HeaderSlotProvider value={headerSlot}>
      <div data-print="flow" className="flex h-dvh flex-col">
        <a
          data-print="hide"
          href="#main"
          className="sr-only z-50 bg-navy-950 px-4 py-2 text-white focus:not-sr-only focus:absolute focus:top-2 focus:left-2"
          onClick={(e) => {
            e.preventDefault();
            mainRef.current?.focus();
          }}
        >
          Skip to main content
        </a>
        {/* The library holds only real documents, so the demo notice does not apply there. */}
        {location.pathname.startsWith('/library') ? null : (
          <div data-print="hide">
            <DemoDataBanner />
          </div>
        )}
        {/* Tiled layout (ADR-009): brand, rail, top bar and page sit in separate soft tiles. */}
        <div data-print="flow" className="flex min-h-0 flex-1 gap-2 p-2 lg:gap-3 lg:p-3">
          <aside
            data-print="hide"
            aria-label="Sidebar"
            className="hidden w-28 shrink-0 flex-col gap-3 lg:flex"
          >
            <Link
              to={EXPERIENCE_META[experience].home}
              className="flex h-14 shrink-0 items-center justify-center rounded-xl border border-line-200 bg-white"
            >
              <Brand size="sm" showWordmark={false} />
              <span className="sr-only"> home</span>
            </Link>
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-xl border border-line-200 bg-white px-2 py-4">
              <NavList items={items} variant="rail" />
            </div>
          </aside>

          <div data-print="flow" className="flex min-w-0 flex-1 flex-col gap-2 lg:gap-3">
            <header
              data-print="hide"
              className="flex h-14 shrink-0 items-center gap-2 rounded-xl border border-line-200 bg-white px-3 md:gap-3 md:px-4"
            >
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                ref={menuButtonRef}
                aria-label="Open navigation"
                onClick={() => setMobileNavOpen(true)}
              >
                <Menu aria-hidden />
              </Button>
              <WorkspaceSwitcher current={experience} />
              <PrivacyScopeIndicator current={experience} />
              <div className="ml-auto flex items-center gap-2">
                <div ref={setHeaderSlot} className="flex items-center empty:hidden" />
                <Link
                  to="/feedback"
                  state={{ from: location.pathname }}
                  className="flex items-center gap-1 px-2 text-sm font-medium text-navy-900 underline-offset-2 hover:underline"
                >
                  <MessageSquare className="size-4" aria-hidden />
                  <span className="sr-only sm:not-sr-only">Feedback</span>
                </Link>
                <span className="hidden text-sm whitespace-nowrap text-muted-600 xl:inline">
                  {session?.displayName}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    signOut();
                    void navigate('/');
                  }}
                >
                  <LogOut aria-hidden />
                  <span className="sr-only lg:not-sr-only">Sign out</span>
                </Button>
              </div>
            </header>

            <div
              data-print="flow"
              className="flex min-h-0 flex-1 overflow-hidden rounded-xl border border-line-200 bg-white"
            >
              <main
                data-print="flow"
                id="main"
                ref={mainRef}
                tabIndex={0}
                className="min-w-0 flex-1 overflow-y-auto focus-visible:outline-offset-[-2px]"
              >
                <Outlet />
              </main>
              {citation && isWide ? (
                <aside
                  data-print="hide"
                  aria-labelledby="inspector-heading"
                  className="flex w-[26rem] shrink-0 flex-col overflow-y-auto border-l border-line-200 bg-white"
                >
                  <div className="flex items-center justify-between border-b border-line-200 p-4">
                    <h2
                      id="inspector-heading"
                      ref={inspectorHeadingRef}
                      tabIndex={-1}
                      className="font-serif text-lg font-semibold text-navy-900 focus:outline-none"
                    >
                      Source inspector
                    </h2>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Close source inspector"
                      onClick={close}
                    >
                      <X aria-hidden />
                    </Button>
                  </div>
                  <div className="p-4">
                    <SourceInspector citation={citation} />
                  </div>
                </aside>
              ) : null}
            </div>
          </div>
        </div>

        {!isWide ? (
          <Sheet
            open={citation !== null}
            onOpenChange={(open) => !open && close()}
            title="Source inspector"
            side={isTablet ? 'right' : 'bottom'}
          >
            {citation ? <SourceInspector citation={citation} /> : null}
          </Sheet>
        ) : null}

        <Sheet
          open={mobileNavOpen}
          returnFocusRef={menuButtonRef}
          onOpenChange={setMobileNavOpen}
          title="Navigation"
          side="left"
        >
          <NavList items={items} variant="list" onNavigate={() => setMobileNavOpen(false)} />
        </Sheet>
      </div>
    </HeaderSlotProvider>
  );
}
